import React, { useCallback, useEffect, useState } from 'react';
import './otpSection.scss';
import { connect } from 'react-redux';
import { updateUserData, verifyOTP, resendOtp } from '../../redux/action/user'
import TextBox from '../../atoms/textbox';
import ButtonInput from '../../atoms/button';
import { useNavigate } from "react-router-dom";
import LabelInput from '../../atoms/label';
import SnackBar from '../../atoms/snackBar';
import { parsePhoneNumber } from 'react-phone-number-input';
import { useBrowserFingerprint } from '../../pages/login/useBFingerprint';
import { Box, Button, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const OTP_SESSION_KEY = 'japa_otp_session';
// Temporary: 2-minute resend cooldown for testing (OTP validity remains server-side, 15 min).
const RESEND_COOLDOWN_MS = 2 * 60 * 1000;

const confirmModalStyle = {
    overflow: 'hidden',
    position: 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '90vh',
    width: 'min(92vw, 480px)',
    backgroundColor: 'white',
    boxShadow: 24,
    pb: 2,
    borderRadius: '8px',
};

const parseExpiresFromHash = (hash) => {
    if (!hash || typeof hash !== 'string') return null;
    const parts = hash.split('.');
    if (parts.length < 2) return null;
    const expires = parseInt(parts[parts.length - 1], 10);
    return Number.isFinite(expires) ? expires : null;
};

const formatCountdown = (ms) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const saveOtpSession = ({ hash, expiresAt, isLogin }) => {
    try {
        sessionStorage.setItem(
            OTP_SESSION_KEY,
            JSON.stringify({ hash, expiresAt, isLogin })
        );
    } catch {
        // ignore storage errors
    }
};

const readOtpSession = () => {
    try {
        const raw = sessionStorage.getItem(OTP_SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const OtpSection = (props) => {
    const fingerprint = useBrowserFingerprint()

    const navigate = useNavigate();
    const [enableButton, setEnableButton] = useState(true);
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState('error');
    const [expiresAt, setExpiresAt] = useState(null);
    const [remainingMs, setRemainingMs] = useState(null);
    const [openResendModal, setOpenResendModal] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    const lang = props.user.language?.otp;
    const canResend =
        remainingMs !== null &&
        remainingMs <= 0 &&
        !resendLoading &&
        !props.user?.isLoading;

    const syncExpiryFromHash = useCallback((hash) => {
        const session = readOtpSession();
        // Keep existing cooldown if same hash (e.g. page refresh); otherwise start a fresh 1-min window.
        const nextExpires =
            session?.hash === hash && session?.expiresAt
                ? session.expiresAt
                : Date.now() + RESEND_COOLDOWN_MS;
        setExpiresAt(nextExpires);
        setRemainingMs(Math.max(0, nextExpires - Date.now()));
        saveOtpSession({
            hash,
            expiresAt: nextExpires,
            isLogin: !!props.isLoginPage,
        });
    }, [props.isLoginPage]);

    useEffect(() => {
        if (props.user?.otpError) {
            setSnackSeverity('error');
            setErrorMessage(props.user?.otpError)
        }
    }, [props.user?.otpError]);

    useEffect(() => {
        props.updateUserData({
            ...props.user,
            otpError: "",
            otp: ''
        });

        setErrorMessage('')

        const session = readOtpSession();
        if (props.user?.hash) {
            syncExpiryFromHash(props.user.hash);
        } else if (session?.hash) {
            props.updateUserData({
                ...props.user,
                hash: session.hash,
                otpError: "",
                otp: '',
            });
            const restoredExpires = session.expiresAt || parseExpiresFromHash(session.hash);
            if (restoredExpires) {
                setExpiresAt(restoredExpires);
                setRemainingMs(Math.max(0, restoredExpires - Date.now()));
            }
        }
    }, [])

    useEffect(() => {
        if (props.user?.hash) {
            syncExpiryFromHash(props.user.hash);
        }
    }, [props.user?.hash, syncExpiryFromHash]);

    useEffect(() => {
        if (!expiresAt) return undefined;
        const tick = () => setRemainingMs(Math.max(0, expiresAt - Date.now()));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [expiresAt]);

    const redirectToPage = (page) => {
        if (props.changePage)
            props.changePage(page)
    }

    const redirectBack = () => {
        redirectToPage(props.isLoginPage ? 'login' : 'signup')

        setTimeout(() => {
            props.updateUserData({
                ...props.user,
                registrationError: "",
                loginError: "",
                status: props.isLoginPage ? 'login' : 'signup',
            });
        }, 1500);
    }

    useEffect(() => {
        if (props.user?.registrationError) {
            setSnackSeverity('error');
            setErrorMessage(props.user?.registrationError)
            redirectBack();
        }
    }, [props.user?.registrationError])

    useEffect(() => {
        if (props.user?.loginError) {
            setSnackSeverity('error');
            setErrorMessage(props.user?.loginError)
            redirectBack()
        }
    }, [props.user?.loginError])

    useEffect(() => {
        if (errorMessage) {
            setOpenSnackBar(true);
        }
    }, [errorMessage])

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
        setErrorMessage("");
    }

    const otpChange = (value) => {
        if (value.length > 6) {
            return;
        }

        props.updateUserData({
            ...props.user,
            otp: value
        })

        if (value.length === 6) {
            setEnableButton(false);
        } else {
            setEnableButton(true);
        }
    }

    const goBack = () => {
        try {
            sessionStorage.removeItem(OTP_SESSION_KEY);
        } catch {
            // ignore
        }
        redirectToPage(props.isLoginPage ? 'login' : 'signup')
    }

    const removeCountryCode = (data) => {
        var newStr = data.phone.replace(data.code, '')
        return newStr
    }

    const verfiyClick = () => {
        const tempObj = {
            hash: props.user.hash,
            otp: props.user.otp,
            isLogin: props.isLoginPage
        }

        if (props.isLoginPage) {
            const phoneNumber = removeCountryCode({ code: (props?.user?.signupForm?.countryCode ?? ''), phone: props?.user?.loginForm?.mobileNumber })

            tempObj['emailaddress'] = props.user?.loginForm?.emailaddress;
            tempObj['countryCode'] = (props?.user?.signupForm?.countryCode ?? '');
            tempObj['mobileNo'] = phoneNumber
        } else {
            const phoneNumber = removeCountryCode({ code: props?.user?.signupForm?.countryCode, phone: props?.user?.signupForm?.mobileNumber })

            tempObj['emailaddress'] = props.user?.signupForm?.emailaddress;
            tempObj['countryCode'] = props?.user?.signupForm?.countryCode;
            tempObj['mobileNo'] = phoneNumber
        }
        props.verifyOTP({ ...tempObj, fingerprint }, navigate);
    }

    const buildResendPayload = () => {
        if (props.isLoginPage) {
            const phone = parsePhoneNumber(props.user?.loginForm?.mobileNumber || '');
            const phoneNumber = removeCountryCode({
                code: phone?.countryCallingCode ?? '',
                phone: props?.user?.loginForm?.mobileNumber || '',
            });
            return {
                emailaddress: props.user?.loginForm?.emailaddress || '',
                countryCode: phone?.countryCallingCode ?? '',
                mobileNo: phoneNumber,
            };
        }

        const phoneNumber = removeCountryCode({
            code: props?.user?.signupForm?.countryCode || '',
            phone: props?.user?.signupForm?.mobileNumber || '',
        });
        return {
            firstName: props.user?.signupForm?.firstName,
            lastName: props.user?.signupForm?.lastName,
            emailaddress: props.user?.signupForm?.emailaddress,
            hostName: window.location.hostname,
            mobileNumber: phoneNumber,
            countryCode: props?.user?.signupForm?.countryCode,
        };
    };

    const handleResendClick = () => {
        if (!canResend) return;
        setOpenResendModal(true);
    };

    const handleResendCancel = () => {
        setOpenResendModal(false);
    };

    const handleResendContinue = async () => {
        setOpenResendModal(false);
        setResendLoading(true);
        try {
            const hash = await props.resendOtp({
                isLogin: !!props.isLoginPage,
                payload: buildResendPayload(),
            });
            if (hash) {
                syncExpiryFromHash(hash);
                setEnableButton(true);
                setSnackSeverity('success');
                setErrorMessage(lang?.resendSuccess || 'A new OTP has been sent to your email.');
            }
        } catch {
            setSnackSeverity('error');
            setErrorMessage(lang?.resendFailed || 'Unable to resend OTP. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    useEffect(() => {
        if (props.user?.otpSuccess) {
            try {
                sessionStorage.removeItem(OTP_SESSION_KEY);
            } catch {
                // ignore
            }
            if (localStorage.getItem('progressDone') === null) {
                navigate('/progress');
            } else if (localStorage.getItem("detailDone") === null) {
                navigate('/profiledetail');
            } else {
                navigate('/result');
            }
        }
    }, [props.user?.otpSuccess])

    return (
        <div className="login_form position-relative">
            <div onClick={() => goBack()} className='back-left-wrap cursor-pointer'>
                Back
            </div>
            <div className="container-fluid">
                <div className="row">
                    <h2 className='white-color avenir-bold'>{lang?.title}</h2>
                    <p className='subTitle'>
                        {lang?.subTitle}
                    </p>
                    <p className='subTitle1 otp-info-message'>
                        {lang?.subTitle1}
                    </p>
                    <p className='otp-spam-warning'>
                        {lang?.spamWarning}
                    </p>
                </div>
                <LabelInput text={lang?.otpField?.title} />
                <TextBox
                    className='otpTextBox'
                    placeholder={lang?.otpField?.placeHolder}
                    value={props.user?.otp}
                    valueChange={otpChange} />

                <div className="otp-resend-row">
                    {canResend ? (
                        <button
                            type="button"
                            className="otp-resend-link"
                            onClick={handleResendClick}
                        >
                            {lang?.resendOtp || 'Resend OTP'}
                        </button>
                    ) : remainingMs !== null ? (
                        <p className="otp-resend-countdown">
                            {lang?.resendAvailableIn || 'Resend OTP available in'}{' '}
                            {formatCountdown(remainingMs)}
                        </p>
                    ) : null}
                </div>

                <div className="row">
                    <ButtonInput
                        class={`assignment-btn ${enableButton ? 'deactive-btn' : 'active-btn'}`}
                        text={props.user.isNewUser ? lang?.beingAssessmentButtonText : lang?.submitButtonText}
                        onSubmit={verfiyClick}
                        disabled={enableButton} />
                </div>
                <div style={{ textAlign: 'center' }}>
                    <p className='white bottom-section'>
                        {props.user.language?.support?.text}
                        <span className="yellow-font" style={{ marginLeft: '5px' }}>
                            <a className='yellow-font sign-up-welcome' href={`mailto: ${props.user.language?.support?.email}`}>
                                {props.user.language?.support?.email}
                            </a>
                        </span>
                    </p>
                </div>
            </div>

            <Modal open={openResendModal} onClose={handleResendCancel}>
                <Box sx={confirmModalStyle}>
                    <Box className="otp-resend-modal__header">
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 'bold', color: '#fff', letterSpacing: '1px' }}
                        >
                            {lang?.resendConfirmTitle || 'Resend OTP'}
                        </Typography>
                        <CloseIcon onClick={handleResendCancel} className="otp-resend-modal__close" />
                    </Box>
                    <Box sx={{ px: 3, py: 2 }}>
                        <Typography className="otp-spam-warning otp-spam-warning--modal">
                            {lang?.spamWarning}
                        </Typography>
                        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3} flexWrap="wrap">
                            <Button
                                variant="outlined"
                                onClick={handleResendCancel}
                                sx={{ textTransform: 'none', minWidth: 100 }}
                            >
                                {lang?.cancel || 'Cancel'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleResendContinue}
                                sx={{
                                    textTransform: 'none',
                                    minWidth: 100,
                                    background: '#FEBD36',
                                    color: '#1a1a14',
                                    '&:hover': { background: '#E5A82B' },
                                }}
                            >
                                {lang?.continue || 'Continue'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <SnackBar
                open={openSnackBar}
                message={errorMessage}
                severity={snackSeverity}
                handleCloseSnackBar={handleCloseSnackBar}
            />
        </div>
    );
};

OtpSection.propTypes = {};
OtpSection.defaultProps = {};

const actionCreators = {
    updateUserData,
    verifyOTP,
    resendOtp,
};

export default connect(
    ({ user }) => ({
        user,
    }),
    actionCreators
)(OtpSection);
